/**
 * Single hook that resolves initial wizard values for all three pre-fill paths:
 *
 *   1. Adhoc  — no API. Writes ADHOC_PAYLOAD_DEFAULTS into payloadRef.
 *   2. Create — GET /ats_interviews/new. Writes hydratePlanDefaults into payloadRef.
 *   3. Edit   — GET /ats_interviews/:id or /ats_schedules/:id.
 *               Writes hydratePayloadFromApi into payloadRef.
 *
 * payloadRef is fully hydrated for ALL steps before any step form mounts,
 * making create and edit paths symmetric.
 *
 * Replaces: useEditInterviewFetch + useEditPrefill + useInterviewPlanDefaults.
 */
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useGetInterview } from '@/api/useInterviews';
import { useGetSelfSchedule } from '@/api/useSelfSchedule';
import { useShowToast } from '@/hooks/useShowToast';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
} from '../config/types';
import { useKFlags } from '@/hooks/use-k-flags';
import { isAdhocMode, isSelfScheduleMode } from '../config/modeClassifiers';
import { useInterviewPlan } from '../api/useInterviewPlan';
import { useMeetingRoomsAccountSetting } from '../api/useMeetingRoomsAccountSetting';
import { ADHOC_PAYLOAD_DEFAULTS } from './uiDefaults';
import { hydratePlanDefaults } from './fromPlan';
import { hydratePayloadFromApi } from './fromEditApi';

interface UseWizardInitialValuesOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  stageId?: number;
  stageActivityId?: number;
  candidateId?: number;
  interviewId?: number;
  scheduleId?: number;
  /** Adhoc launches from a platform-specific candidate-action menu entry
   *  (e.g. "Send HackerRank interview") — seeds the location so the
   *  picker opens on that platform. */
  interviewPlatform?: 'hackerrank';
}

/** Zoom-status shape lifted off the plan response so adhoc create can paint
 *  the Host (Zoom) chip without exposing the rest of `interviewPlan` to
 *  adhoc consumers (which assume it's `undefined`). */
export interface CurrentUserZoomStatus {
  can_host_zoom_meeting?: boolean;
  zoom_rate_limit_reached?: boolean;
  zoom_plan?: number;
  zoom_plan_type?: string;
}

interface UseWizardInitialValuesResult {
  payloadRef: React.MutableRefObject<ScheduleInterviewPayload>;
  prefillVersion: number;
  /** Raw API response kept as a prop for behavioral flags
   *  (shared_calendar_enabled, minimum_notice_config, zoom_status, etc.).
   *  Initial VALUES come from payloadRef — not from this object.
   *  Always `undefined` for adhoc — consumers rely on this. Adhoc surfaces
   *  the current user's zoom status separately via `currentUserZoomStatus`. */
  interviewPlan: Record<string, unknown> | undefined;
  /** Adhoc-only: current user's zoom status pulled from the plan response
   *  the application's stage points to. Mirrors legacy `SchZoomHost` which
   *  always had the plan API available (legacy adhoc passed
   *  `scheduleType="manual"` so the plan fetch ran). Undefined for
   *  non-adhoc flows (those read zoom status off `interviewPlan` directly). */
  currentUserZoomStatus?: CurrentUserZoomStatus;
  /** Adhoc-only: plan-level zoom host strategy ('organizer' vs 'interviewer')
   *  pulled from the same plan response. Drives `pickAutoHostId` priority —
   *  legacy adhoc respected this account setting; the new wizard's adhoc was
   *  dropping it on the floor and always defaulting to interviewer-first. */
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  /** Adhoc-only: fresh per-stage note-taker mandate lifted off the plan
   *  response the adhoc flow already fetches (the same plan that carries the
   *  Zoom host status). Adhoc nulls `interviewPlan`, so DetailsStep + the
   *  details schema can't read `note_taker_mandatory` off it and would
   *  otherwise fall back to the staleness-prone `atsJobCreate.currentJob`
   *  slice — which the candidate/adhoc entry never populates (only job-admin
   *  screens dispatch `getCurrentJob`), so a mandated virtual-location save
   *  trips `err_note_taker_mandatory` with no recovery (ENG-3339). Undefined
   *  for non-adhoc flows, which read the mandate off `interviewPlan`. */
  planNoteTakerMandatory?: boolean;
  /** Account-level "meeting rooms" gate, resolved uniformly for every
   *  mode. Drives the "Book a meeting room" checkbox + room-availability
   *  fetch in DetailsForm. Plan / edit read it off `interviewPlan`; adhoc
   *  (no plan) fetches the account setting directly — so the checkbox
   *  hides when an admin disabled meeting rooms. */
  meetingRoomsEnabled: boolean;
  showCreateLoader: boolean;
  showEditLoader: boolean;
  showEditError: boolean;
}

export function useWizardInitialValues({
  mode,
  apiReference,
  stageId,
  stageActivityId,
  candidateId,
  interviewId,
  scheduleId,
  interviewPlatform,
}: UseWizardInitialValuesOptions): UseWizardInitialValuesResult {
  const isEdit = apiReference === 'edit';
  const isAdhoc = isAdhocMode(mode);
  const isSelfSchedule = isSelfScheduleMode(mode);

  // Note-taker mandate fallback for ADHOC only — adhoc seeds synchronously
  // before any plan is fetched, so it has no fresh `note_taker_mandatory` to
  // read and falls back to the shared `currentJob` slice. The plan (create)
  // and edit paths below seed from their own FRESH responses instead, and
  // the field-level lock lives in `useNoteTakerConfig` (fed the fresh value
  // via the details schema). When mandatory, the seed is forced `true`.
  const { currentJob = {} } = useSelector(getAtsJobCreate, shallowEqual);
  const isNoteTakerMandatory = Boolean(
    (currentJob as Record<string, unknown>)?.data?.note_taker?.mandatory
  );

  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  // ── Path 1: Adhoc ──────────────────────────────────────────────────
  // No API call. Seed payloadRef synchronously on first render so the
  // form mounts with the correct blank defaults.
  const adhocInitRef = useRef(false);
  if (isAdhoc && !adhocInitRef.current) {
    adhocInitRef.current = true;
    payloadRef.current = {
      ...ADHOC_PAYLOAD_DEFAULTS,
      details: { ...ADHOC_PAYLOAD_DEFAULTS.details },
    };
    if (isNoteTakerMandatory) {
      payloadRef.current.details.enable_note_taker = true;
    }
    // Platform-specific adhoc entry points (e.g. "Send HackerRank interview"
    // candidate action) seed location so the picker opens on that platform.
    // The schema also receives `interviewPlatform` and disables the picker.
    if (interviewPlatform === 'hackerrank') {
      payloadRef.current.details.location = 'hackerrank';
    }
  }
  if (!isAdhoc) {
    adhocInitRef.current = false;
  }

  // ── Path 3: Edit — fetch ────────────────────────────────────────────
  const useScheduleGet = isEdit && isSelfSchedule && !!scheduleId;
  const toast = useShowToast();

  const {
    data: interviewFromInterviewsEndpoint,
    isLoading: isLoadingInterview,
    isError: isInterviewLoadError,
    isFetched: isFetchedInterview,
    dataUpdatedAt: dataUpdatedAtInterview,
    isFetching: isFetchingInterview,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !useScheduleGet && !!interviewId,
  });

  const {
    data: interviewFromSchedulesEndpoint,
    isLoading: isLoadingSchedule,
    isError: isScheduleLoadError,
    isFetched: isFetchedSchedule,
    dataUpdatedAt: dataUpdatedAtSchedule,
    isFetching: isFetchingSchedule,
  } = useGetSelfSchedule(scheduleId, {
    enabled: useScheduleGet,
  });

  const existingInterview = useScheduleGet
    ? interviewFromSchedulesEndpoint
    : interviewFromInterviewsEndpoint;
  const isLoadingExisting = useScheduleGet
    ? isLoadingSchedule
    : isLoadingInterview;
  const isLoadError = useScheduleGet
    ? isScheduleLoadError
    : isInterviewLoadError;
  const isFetchedExisting = useScheduleGet
    ? isFetchedSchedule
    : isFetchedInterview;
  // React Query timestamp of the latest successful fetch for the active
  // endpoint. Changes when a refetch lands fresh data — used to re-seed
  // payloadRef on reopen so the stale-while-revalidate cache can't pin
  // the form to an out-of-date snapshot (host / location / etc.).
  const existingDataUpdatedAt = useScheduleGet
    ? dataUpdatedAtSchedule
    : dataUpdatedAtInterview;
  const isFetchingExisting = useScheduleGet
    ? isFetchingSchedule
    : isFetchingInterview;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ── Path 3: Edit — hydrate payloadRef ──────────────────────────────
  const lastHydratedIdRef = useRef<number | null>(null);
  // `dataUpdatedAt` of the response payloadRef was last hydrated from.
  // Re-hydration keys on this in addition to the id so a fresh refetch
  // of the SAME interview (reopen → stale-while-revalidate serves cache,
  // then background refetch lands fresh data) is actually applied
  // instead of being dropped by an id-only guard. Fixes the systemic
  // "stale on immediate re-edit" class (host, location, …).
  const lastHydratedAtRef = useRef<number>(0);
  // The stale→fresh upgrade is applied AT MOST ONCE per mount. The
  // wizard unmounts on close (`InterviewAction` renders it behind
  // `isOpen`), so every open is a fresh mount and gets exactly one
  // "cache → fresh refetch" re-seed. A LATER refetch within the same
  // open (e.g. `refetchOnReconnect` — TanStack default `true`, the
  // global config only disables `refetchOnWindowFocus`) must NOT
  // re-hydrate: that would wipe the user's in-progress edits. The old
  // id-only guard absorbed this implicitly; this ref restores that
  // protection while still applying the one fresh upgrade.
  const freshUpgradeUsedRef = useRef(false);
  const prefillGenerationRef = useRef(0);
  // Reference of the response payloadRef was last hydrated FROM. React Query
  // structural sharing keeps the SAME object reference when a refetch returns
  // deeply-equal data, so this distinguishes a genuine data change from an
  // identical `refetchOnMount` round-trip — letting us skip the
  // remount-causing re-hydrate for the latter (the drawer-flash-on-open fix).
  const lastHydratedDataRef = useRef<unknown>(null);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
    lastHydratedAtRef.current = 0;
    freshUpgradeUsedRef.current = false;
    lastHydratedDataRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  const { feInterviewBuffer, feManualInterviewBuffer } = useKFlags();

  const idChanged =
    editDataReady && existingInterview.id !== lastHydratedIdRef.current;
  // One-shot stale→fresh upgrade: a new `dataUpdatedAt` for the SAME
  // interview, only until consumed for this mount. `!isFetchingExisting`
  // at id-hydration means the data was already fresh (no cache served
  // first) → consume the upgrade immediately so a later reconnect
  // refetch can't clobber edits.
  const freshUpgrade =
    editDataReady &&
    !idChanged &&
    !freshUpgradeUsedRef.current &&
    existingDataUpdatedAt !== lastHydratedAtRef.current;

  if (idChanged || freshUpgrade) {
    if (freshUpgrade || !isFetchingExisting) {
      freshUpgradeUsedRef.current = true;
    }
    lastHydratedIdRef.current = existingInterview.id;
    lastHydratedAtRef.current = existingDataUpdatedAt;
    // Only re-hydrate + bump `prefillVersion` (which REMOUNTS the keyed step
    // subtrees — CalendarPanel, editor, scorecard, via `key={prefillVersion}`)
    // when the interview data ACTUALLY changed. React Query structural
    // sharing leaves `existingInterview` referentially stable when the
    // identical `refetchOnMount` round-trip returns deeply-equal data, so an
    // unchanged refetch no longer remounts the heavy tree and flashes the
    // drawer on open. The one-shot `freshUpgradeUsedRef` is still consumed
    // above, so edit-clobber protection on later refetches is unchanged.
    if (existingInterview !== lastHydratedDataRef.current) {
      lastHydratedDataRef.current = existingInterview;
      prefillGenerationRef.current += 1;
      payloadRef.current = hydratePayloadFromApi(existingInterview, undefined, {
        feInterviewBuffer: Boolean(feInterviewBuffer),
        feManualInterviewBuffer: Boolean(feManualInterviewBuffer),
      });
      // Force-on from the FRESH per-stage mandate on the existing interview
      // (`note_taker_mandatory`), not the staleness-prone currentJob slice.
      if (
        (existingInterview as { note_taker_mandatory?: boolean } | undefined)
          ?.note_taker_mandatory
      ) {
        payloadRef.current.details.enable_note_taker = true;
      }
    }
  }

  // ── Path 2: Create — fetch plan ────────────────────────────────────
  // Fetch whenever a stageId is available — including adhoc. Legacy parity:
  // legacy adhoc routed through `scheduleType="manual"` + `isAdhoc={true}`
  // so the plan query always ran (gate was on schedule type, not isAdhoc).
  // The plan response carries the current user's Zoom status; adhoc needs
  // it to paint the Host chip + auto-pick the recruiter as host.
  //
  // Adhoc still owns its own defaults — `hydratePlanDefaults` and the
  // `interviewPlan` return value remain gated by `!isAdhoc` below so no
  // plan-derived behavior leaks into adhoc forms. Only the Zoom-status
  // slice is surfaced (via `currentUserZoomStatus`).
  const planEnabled = !isEdit && !!stageId && stageId > 0;

  const { data: planData, isLoading: isLoadingPlan } = useInterviewPlan(
    {
      stageId: stageId ?? 0,
      stageActivityId: stageActivityId ?? undefined,
      contactId: candidateId ?? undefined,
    },
    { enabled: planEnabled }
  );

  // Hydrate payloadRef once when plan resolves so ALL steps have their
  // defaults before any form mounts (gated by showCreateLoader).
  const planHydratedRef = useRef(false);
  if (!isEdit) planHydratedRef.current = false;
  if (!isEdit && !isAdhoc && planData && !planHydratedRef.current) {
    planHydratedRef.current = true;
    payloadRef.current = hydratePlanDefaults(planData, mode, {
      feInterviewBuffer: Boolean(feInterviewBuffer),
      feManualInterviewBuffer: Boolean(feManualInterviewBuffer),
    });
    // `hydratePlanDefaults` already seeds `enable_note_taker` from the FRESH
    // plan (`note_taker_mandatory || note_taker_enabled`). Keep the explicit
    // force on the same fresh value — never the stale currentJob slice.
    if (planData?.note_taker_mandatory) {
      payloadRef.current.details.enable_note_taker = true;
    }
  }

  // ── Derive interviewPlan for behavioral flags ──────────────────────
  // Edit: use the raw existing interview (feature flags live there).
  // Create: use the raw plan response.
  // Adhoc: undefined — downstream consumers branch on this; flipping to
  // truthy would silently activate plan-derived behavior in adhoc
  // (shared_calendar_enabled, meeting_rooms_enabled, minimum_notice_*,
  // zoom_host_type, calendar_info, comm-template prefill, etc.).
  const interviewPlan: Record<string, unknown> | undefined = isEdit
    ? (existingInterview as Record<string, unknown> | undefined)
    : isAdhoc
      ? undefined
      : planData;

  // ── Derive current user's Zoom status (adhoc only) ─────────────────
  // The plan response emits zoom data in two shapes — nested under
  // `user_zoom_status` (typically edit / specific create paths) and as
  // flat root-level fields (the `_zoom_status.json.jbuilder` partial at
  // the root of `ats_interviews/new`). DetailsStep already handles both
  // shapes off `interviewPlan` for non-adhoc; here we surface only the
  // current-user slice so adhoc gets the chip without re-exposing the
  // rest of the plan.
  const currentUserZoomStatus = useMemo<
    CurrentUserZoomStatus | undefined
  >(() => {
    if (!isAdhoc || !planData) return undefined;
    const root = planData as Record<string, unknown>;
    const nested = root.user_zoom_status as
      | Record<string, unknown>
      | null
      | undefined;
    if (nested && typeof nested === 'object') {
      return {
        can_host_zoom_meeting: nested.can_host_zoom_meeting as
          | boolean
          | undefined,
        zoom_rate_limit_reached: nested.zoom_rate_limit_reached as
          | boolean
          | undefined,
        zoom_plan: nested.zoom_plan as number | undefined,
        zoom_plan_type: nested.zoom_plan_type as string | undefined,
      };
    }
    if (
      'can_host_zoom_meeting' in root ||
      'zoom_plan' in root ||
      'zoom_rate_limit_reached' in root
    ) {
      return {
        can_host_zoom_meeting: root.can_host_zoom_meeting as
          | boolean
          | undefined,
        zoom_rate_limit_reached: root.zoom_rate_limit_reached as
          | boolean
          | undefined,
        zoom_plan: root.zoom_plan as number | undefined,
        zoom_plan_type: root.zoom_plan_type as string | undefined,
      };
    }
    return undefined;
  }, [isAdhoc, planData]);

  // Plan-level Zoom host strategy. Same gating + sourcing as
  // `currentUserZoomStatus` — only surfaced in adhoc so non-adhoc consumers
  // keep reading from `interviewPlan?.zoom_host_type` unchanged.
  const planZoomHostType = useMemo<
    'organizer' | 'interviewer' | string | null | undefined
  >(() => {
    if (!isAdhoc || !planData) return undefined;
    const raw = (planData as Record<string, unknown>).zoom_host_type;
    if (raw === null) return null;
    if (typeof raw === 'string') return raw;
    return undefined;
  }, [isAdhoc, planData]);

  // Adhoc note-taker mandate off the FRESH plan (correctly scoped per
  // job + stage), same sourcing/gating as `planZoomHostType`. Adhoc never
  // fetches the `currentJob` slice the mandate guards fall back to, so the
  // toggle would render unlocked/off and a virtual-location save trips
  // `err_note_taker_mandatory` (ENG-3339). Surfacing only this slice keeps
  // the rest of the plan hidden from adhoc consumers. `undefined` until the
  // plan lands → guards keep their `currentJob` fallback until then.
  const planNoteTakerMandatory = isAdhoc
    ? (planData as { note_taker_mandatory?: boolean } | undefined)
        ?.note_taker_mandatory
    : undefined;

  // Adhoc has no interview plan to carry `meeting_rooms_enabled`, and its
  // plan response doesn't reliably surface the flag — so adhoc fetches the
  // account-level admin setting straight from its settings endpoint.
  // Disabled (no fetch) for plan / edit flows, which read it off the plan.
  const { data: adhocMeetingRoomsEnabled } = useMeetingRoomsAccountSetting({
    enabled: isAdhoc,
  });

  // Account-level meeting-rooms gate, resolved for every mode. Edit reads
  // the existing interview; create/activity reads the plan response
  // (`interviewPlan === planData`); adhoc nulls `interviewPlan`, so falls
  // through to the direct account-setting fetch. `??` (not `||`) so an
  // explicit `false` from a plan / edit response is honored.
  const meetingRoomsEnabled = Boolean(
    interviewPlan?.meeting_rooms_enabled ?? adhocMeetingRoomsEnabled
  );

  return {
    payloadRef,
    prefillVersion: isEdit ? prefillGenerationRef.current : 0,
    interviewPlan,
    currentUserZoomStatus,
    planZoomHostType,
    planNoteTakerMandatory,
    meetingRoomsEnabled,
    // Adhoc mounts instantly with ADHOC_PAYLOAD_DEFAULTS — never gate the
    // form on the plan fetch landing. The plan fetch is fire-and-forget
    // in adhoc; the chip simply paints when it arrives.
    showCreateLoader: planEnabled && !isAdhoc && isLoadingPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
  };
}
